/**
 * Request Session 信息展示组件
 * 根据 subMessageId 通过 inquiryCardPost 获取数据并展示邀约后的 request session 信息
 *
 * UI 与 InquiryCard（Figma 8970-110533）保持一致，适配字段差异。
 */

import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import ReactDOM from 'react-dom';
import cn from 'classnames';
import { useThrottleFn } from 'ahooks';
import { MessageCircle } from 'lucide-react';
import { Button, Skeleton, toast } from '@leadsnavi/ui-kit';
import { InquiryStatusBadge } from '@/common/biz/status-tag/InquiryStatusBadge';
import type { IApiSessionInquiryCardResp, IApiInquiryDto } from '@/services/swagger/easourcing/models';
import { inquiryCardPost } from '@/services/swagger/easourcing/SessionService';
import { operateInquiryPost } from '@/services/swagger/easourcing/InquiryService';
import { PageDialogWrapper } from '@/features/chat-messages/components/base/pageDialogWrapper';
import { usePushMessage, PushEventType } from '@/hooks/useWebSocket';
import mixpanel from 'mixpanel-browser';
import { isInComputation, ComputingText } from '@/features/inquiries/components/inquiry-card';
import { InquiryLog } from '@/features/inquiries/components/inquiry-log';
import { AgentProgressPage } from '@/features/agent-progress';
import ActiveTaskLimitModal from '@/components/active-task-limit-modal';

// ---------------------------------------------------------------------------
// Status mapping (mirrors InquiryCard logic)
// ---------------------------------------------------------------------------

type StatusKind = 'running' | 'paused' | 'completed';

function getStatusKind(status: number | undefined): StatusKind {
  switch (status) {
    case 1:
      return 'paused';
    case 2:
      return 'completed';
    default:
      return 'running';
  }
}

// ---------------------------------------------------------------------------
// Progress calculation (same algorithm as InquiryCard)
// ---------------------------------------------------------------------------

function calcProgress(data: IApiSessionInquiryCardResp | null): number {
  if (!data || !data.companyTotalCount) return 0;
  if (isInComputation(data)) return 0;
  return Math.round(((data.repliedCompanyCount + data.contactedCompanyCount) / data.companyTotalCount) * 100);
}

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface RequestSessionCardProps {
  /** 子消息 ID，用于 inquiryCardPost 请求数据 */
  subMessageId: string;
  /** 会话ID，用于 operateInquiry 接口 */
  sessionId?: string;
  /** 弹窗挂载容器 Ref，提供时点击详情以弹窗展示，否则跳转页面 */
  dialogWrpperRef?: React.RefObject<HTMLDivElement>;
  /** 自定义类名 */
  className?: string;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const RequestSessionCard: FC<RequestSessionCardProps> = ({
  subMessageId,
  sessionId,
  dialogWrpperRef,
  className = '',
}) => {
  const [loading, setLoading] = useState(true);
  const [isContactProgressVisible, setIsContactProgressVisible] = useState(false);
  const [selectedInquiryId, setSelectedInquiryId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<IApiSessionInquiryCardResp | null>(null);
  const [resuming, setResuming] = useState(false);
  const [showTaskLimitModal, setShowTaskLimitModal] = useState(false);
  const [runningInquiries, setRunningInquiries] = useState<IApiInquiryDto[]>([]);
  const [taskLimitLoading, setTaskLimitLoading] = useState(false);

  // 卡片数据拉取，silent 为 true 时不改 loading/error，用于推送触发的静默刷新
  const fetchCardData = useCallback(
    (opts?: { silent?: boolean }) => {
      if (!subMessageId) return;
      const silent = opts?.silent ?? false;
      if (!silent) {
        setLoading(true);
        setError(null);
      }
      inquiryCardPost({ subMessageId })
        .then(res => {
          const cardData = res?.data as IApiSessionInquiryCardResp | undefined;
          if (cardData) {
            setData(cardData);
            if (silent) setError(null);
          } else if (!silent) {
            setError('Failed to load data');
          }
        })
        .catch(() => {
          if (!silent) setError('Failed to load data');
        })
        .finally(() => {
          if (!silent) setLoading(false);
        });
    },
    [subMessageId]
  );

  useEffect(() => {
    if (!subMessageId) {
      setLoading(false);
      return;
    }
    fetchCardData();
  }, [subMessageId, fetchCardData]);

  // 挂载后延迟 3s 再请求一次同步数据
  useEffect(() => {
    if (!subMessageId) return;
    const timer = setTimeout(() => {
      fetchCardData({ silent: true });
    }, 3000);
    return () => clearTimeout(timer);
  }, [subMessageId, fetchCardData]);

  // 多种推送共享同一个节流刷新，10s 窗口内最多触发首尾各一次
  const { run: throttledRefresh } = useThrottleFn(() => fetchCardData({ silent: true }), {
    wait: 10000,
    leading: true,
    trailing: true,
  });

  const progress = useMemo(() => calcProgress(data), [data]);
  const kind = data ? getStatusKind(data.inquiryStatus) : ('running' as StatusKind);
  // const isRunning = kind === 'running';
  const isPaused = kind === 'paused';

  // Resume 操作
  const handleResume = useCallback(async () => {
    if (!data?.inquiryId) return;
    setResuming(true);
    try {
      const res = await operateInquiryPost({ inquiryId: data.inquiryId, operate: 2, sessionId });
      const runningList = (res?.data?.runningInquiries || []) as IApiInquiryDto[];
      if (runningList.length >= 3) {
        // 任务数量达到上限，需要用户选择替换
        setRunningInquiries(runningList);
        setShowTaskLimitModal(true);
        return;
      }
      // 乐观更新状态为 running
      setData(prev => (prev ? { ...prev, inquiryStatus: 0 } : null));
      fetchCardData({ silent: true });
      toast.success('Inquiry resumed');
    } catch (err) {
      console.error('Failed to resume inquiry:', err);
      toast.error('Failed to resume');
    } finally {
      setResuming(false);
    }
  }, [data?.inquiryId, sessionId, fetchCardData]);

  // 任务限制弹窗：替换一个运行中的任务，恢复当前任务
  const handleReplaceTask = useCallback(
    async (runningInquiryId: string) => {
      if (!data?.inquiryId) return;
      setTaskLimitLoading(true);
      try {
        await operateInquiryPost({ inquiryId: runningInquiryId, operate: 1 });
        await operateInquiryPost({ inquiryId: data.inquiryId, operate: 2, sessionId });
        setShowTaskLimitModal(false);
        setData(prev => (prev ? { ...prev, inquiryStatus: 0 } : null));
        fetchCardData({ silent: true });
        toast.success('Inquiry resumed');
      } catch (err) {
        console.error('Failed to replace task:', err);
        toast.error('Operation failed');
      } finally {
        setTaskLimitLoading(false);
      }
    },
    [data?.inquiryId, sessionId, fetchCardData]
  );

  // 未读数变化 → 刷新回复数 & 未读红点
  usePushMessage(PushEventType.CONTACT_UNREAD_COUNT, () => {
    throttledRefresh();
  });

  // 会话新消息 → 刷新回复数 & 发送进度
  usePushMessage<{ sessionId: string; totalUnreadNum: number; unreadNum: number }>(
    PushEventType.SESSION_NEW_MSG,
    message => {
      if (sessionId && message.content?.sessionId !== sessionId) return;
      throttledRefresh();
    }
  );

  // 任务运行状态变化 → 刷新 inquiryStatus / StatusTag
  usePushMessage<{ sessionId: string; inquiryTaskRunning: 0 | 1 }>(PushEventType.SESSION_TASK_RUNNING, message => {
    if (sessionId && message.content?.sessionId !== sessionId) return;
    throttledRefresh();
  });

  if (loading) {
    return (
      <div className={`rounded-xl border border-[#E5E5E5] bg-white p-5 ${className}`}>
        <Skeleton className="h-5 w-3/4 mb-3" />
        <Skeleton className="h-[72px] w-full rounded-lg mb-4" />
        <Skeleton className="h-8 w-full" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className={`rounded-xl border border-[#E5E5E5] bg-white p-5 text-[#737373] text-sm ${className}`}>
        {error || 'No data'}
      </div>
    );
  }

  const inComputation = isInComputation(data);
  const title = data.inquiryName || 'Inquiry Status';

  return (
    <div className={cn('flex flex-col gap-2 w-[520px]', className)}>
      {/* ═══════ 上半部分：白底卡片 ═══════ */}
      <div className="flex flex-col rounded-xl border border-[#E5E5E5] bg-white">
        {/* ═══════ Top section: title + badge + progress ═══════ */}
        <div className="flex flex-col gap-4 px-5 pt-5 pb-[10px]">
          {/* Row 1: Title + Badge | Percentage */}
          <div className="flex items-center justify-between">
            <div className="flex min-w-0 items-center gap-3">
              <h3 className="overflow-hidden text-ellipsis whitespace-nowrap text-base font-extrabold leading-6 tracking-[-0.02em] text-[#101828]">
                {title}
              </h3>

              {/* Status badge */}
              <InquiryStatusBadge status={kind} />
            </div>

            {/* Percentage */}
            {!inComputation && (
              <span className="shrink-0 text-base font-normal leading-6 text-[#262626] tabular-nums">{progress}%</span>
            )}
          </div>

          {/* Row 2: Progress bar */}
          {inComputation ? (
            <ComputingText />
          ) : (
            <div
              className="relative h-[10px] w-full overflow-hidden rounded-[13px]"
              style={{ backgroundColor: '#E5E5E5' }}
            >
              <div
                className={cn('absolute left-0 top-0 h-full rounded-[13px] transition-[width] duration-300 ease-out', {
                  'bg-[#262626]': kind === 'paused' || kind === 'running' || kind === 'completed',
                })}
                style={{ width: `${Math.max(0, Math.min(100, progress))}%` }}
              />
            </div>
          )}
        </div>

        {/* ═══════ Bottom section: replies + actions ═══════ */}
        <div className="flex items-center justify-between px-5 pt-[10px] pb-5">
          {/* Replies pill */}
          <div className="flex h-8 items-stretch gap-3">
            <div className="flex items-center gap-3 rounded-3xl bg-[#F8F8F7] px-[14px] py-2">
              <MessageCircle size={16} className="shrink-0 text-[#525252]" />
              <div className="flex items-center gap-1">
                <span className="text-base font-extrabold leading-6 tracking-[-0.02em] text-[#262626]">
                  {data.replies}
                </span>
                <span className="text-sm font-normal leading-[21px] tracking-[0.005em] text-[#525252]">Replies</span>
              </div>
            </div>
          </div>

          {/* View details button */}
          {data.inquiryId && (
            <div className="flex items-center gap-[14px]">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  mixpanel.track('view_inquiry_details_click');
                  setSelectedInquiryId(data.inquiryId);
                  setIsContactProgressVisible(true);
                }}
                className="inline-flex h-8 shrink-0 items-center justify-center gap-[6px] rounded-[8px] border border-[#E5E5E5] bg-white px-4 py-2 text-sm font-medium leading-[21px] tracking-[0.005em] text-[#262626] hover:bg-neutral-50"
              >
                View details
              </Button>
              {isPaused && (
                <Button
                  size="sm"
                  disabled={resuming}
                  onClick={handleResume}
                  className="inline-flex h-8 shrink-0 items-center justify-center rounded-lg bg-[#171717] px-3 py-[5.5px] text-sm font-medium leading-[21px] tracking-[0.005em] text-white hover:bg-[#171717]/90 disabled:opacity-50"
                >
                  Resume
                </Button>
              )}
            </div>
          )}
        </div>
      </div>

      {/* ═══════ 下半部分：InquiryLog 日志区域 ═══════ */}
      {data.inquiryId && !isPaused && (
        <div className="rounded-xl bg-[#F8F8F7]">
          <InquiryLog key={data.inquiryId} inquiryId={data.inquiryId} variant="card" polling={true} />
        </div>
      )}

      {/* 建联进度弹窗 */}
      {isContactProgressVisible && selectedInquiryId && dialogWrpperRef?.current
        ? ReactDOM.createPortal(
            <PageDialogWrapper>
              <AgentProgressPage
                inquiryId={selectedInquiryId}
                sessionId={sessionId}
                onClose={() => {
                  setIsContactProgressVisible(false);
                  setSelectedInquiryId(null);
                }}
                onNameChange={name => setData(prev => (prev ? { ...prev, inquiryName: name } : null))}
                onStateChange={() => fetchCardData({ silent: true })}
              />
            </PageDialogWrapper>,
            dialogWrpperRef.current
          )
        : null}

      {/* 任务限制弹窗 */}
      <ActiveTaskLimitModal
        open={showTaskLimitModal}
        onOpenChange={setShowTaskLimitModal}
        runningInquiries={runningInquiries}
        loading={taskLimitLoading}
        showSaveOption={false}
        onReplace={handleReplaceTask}
      />
    </div>
  );
};
